import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, Trash } from "@phosphor-icons/react";

const STATUS_TONE = {
  draft: "bg-muted text-muted-foreground",
  sent: "bg-blue-500/15 text-blue-400",
  accepted: "bg-emerald-500/15 text-emerald-400",
  rejected: "bg-rose-500/15 text-rose-400",
  invoiced: "bg-primary/15 text-primary",
  paid: "bg-emerald-500/15 text-emerald-400",
};

function fmt(n) { return new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(n || 0); }

const TYPE_LABELS = {
  quote: "Offerte",
  invoice: "Factuur",
};

const STATUS_LABELS = {
  draft: "Concept",
  sent: "Verzonden",
  accepted: "Geaccepteerd",
  rejected: "Afgewezen",
  invoiced: "Gefactureerd",
  paid: "Betaald",
};

const EMPTY = {
  customer_id: "", customer_name: "", customer_email: "", title: "", type: "quote", status: "draft", notes: "",
  items: [{ description: "", quantity: 1, unit_price: 0, tax_rate: 21 }],
};

export default function Offertes() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [type, setType] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [sendDirect, setSendDirect] = useState(true);
  const [quoteMessage, setQuoteMessage] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const params = {};
    if (type !== "all") params.type = type;
    const { data } = await api.get("/quotes", { params });
    setItems(data);
  }, [type]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data || []));
  }, []);

  const selectCustomer = (customerId) => {
    const selected = customers.find((customer) => customer.id === customerId);
    setForm({
      ...form,
      customer_id: selected?.id || "",
      customer_name: selected?.company_name || "",
      customer_email: selected?.email || "",
    });
  };

  const submit = async () => {
    if (!form.title || !form.customer_name) return;
    setSaving(true);
    setQuoteMessage("");
    try {
      const payload = {
        ...form,
        customer_id: form.customer_id || null,
      };
      delete payload.customer_email;
      const { data: quote } = await api.post("/quotes", payload);

      if (sendDirect && form.customer_email) {
        await api.post(`/quotes/${quote.id}/send`, { to: form.customer_email });
      }

      setOpen(false);
      setForm(EMPTY);
      setSendDirect(true);
      load();
    } catch (error) {
      setQuoteMessage(error.response?.data?.detail || "Opslaan of verzenden is mislukt.");
    } finally {
      setSaving(false);
    }
  };

  const addLine = () => setForm({ ...form, items: [...form.items, { description: "", quantity: 1, unit_price: 0, tax_rate: 21 }] });
  const updateLine = (i, k, v) => {
    const next = form.items.map((it, idx) => idx === i ? { ...it, [k]: k === "description" ? v : parseFloat(v || 0) } : it);
    setForm({ ...form, items: next });
  };
  const removeLine = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const subtotal = form.items.reduce((s, it) => s + (it.quantity || 0) * (it.unit_price || 0), 0);
  const tax = form.items.reduce((s, it) => s + (it.quantity || 0) * (it.unit_price || 0) * ((it.tax_rate || 0) / 100), 0);

  return (
    <div className="space-y-6" data-testid="quotes-page">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="overline mb-2">Facturatie</div>
          <h1 className="font-display text-4xl tracking-tight leading-none">Offertes & Facturen</h1>
          <p className="text-muted-foreground text-sm mt-2">Documenten met automatisch berekende totalen.</p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alles</SelectItem>
              <SelectItem value="quote">Offertes</SelectItem>
              <SelectItem value="invoice">Facturen</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={(value) => {
            setOpen(value);
            if (value) setQuoteMessage("");
          }}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-primary text-primary-foreground" data-testid="new-quote-btn">
                <Plus size={14} className="mr-1.5" /> Nieuw
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle className="font-display tracking-tight">Nieuwe {TYPE_LABELS[form.type].toLowerCase()}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4 py-2">
                <div className="space-y-1.5"><Label className="overline">Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({...form, type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="quote">Offerte</SelectItem>
                      <SelectItem value="invoice">Factuur</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label className="overline">Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_LABELS).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label className="overline">Klant *</Label>
                  <Select value={form.customer_id || "__manual"} onValueChange={(value) => value === "__manual" ? setForm({...form, customer_id: "", customer_email: ""}) : selectCustomer(value)}>
                    <SelectTrigger data-testid="quote-customer-select"><SelectValue placeholder="Kies klant" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__manual">Handmatig invullen</SelectItem>
                      {customers.map((customer) => (
                        <SelectItem key={customer.id} value={customer.id}>
                          {customer.company_name}{customer.email ? ` - ${customer.email}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!form.customer_id && (
                    <Input data-testid="quote-customer" value={form.customer_name} onChange={(e) => setForm({...form, customer_name: e.target.value})} placeholder="Klantnaam" />
                  )}
                </div>
                <div className="col-span-2 space-y-1.5"><Label className="overline">Titel *</Label><Input data-testid="quote-title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} /></div>
                {form.customer_id && (
                  <div className="col-span-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm">
                    {form.customer_email ? (
                      <span>Bij opslaan wordt deze {TYPE_LABELS[form.type].toLowerCase()} direct verzonden naar {form.customer_email}.</span>
                    ) : (
                      <span>Deze klant heeft nog geen e-mailadres. De {TYPE_LABELS[form.type].toLowerCase()} wordt alleen opgeslagen.</span>
                    )}
                  </div>
                )}

                <div className="col-span-2 mt-2">
                  <div className="overline mb-2">Regels</div>
                  <div className="space-y-2">
                    {form.items.map((it, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 items-center">
                        <Input className="col-span-5" placeholder="Omschrijving" value={it.description} onChange={(e) => updateLine(i, "description", e.target.value)} />
                        <Input className="col-span-2" type="number" placeholder="Aantal" value={it.quantity} onChange={(e) => updateLine(i, "quantity", e.target.value)} />
                        <Input className="col-span-2" type="number" placeholder="Prijs per stuk" value={it.unit_price} onChange={(e) => updateLine(i, "unit_price", e.target.value)} />
                        <Input className="col-span-2" type="number" placeholder="BTW %" value={it.tax_rate} onChange={(e) => updateLine(i, "tax_rate", e.target.value)} />
                        <Button variant="ghost" size="icon" className="col-span-1" onClick={() => removeLine(i)}><Trash size={14} /></Button>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={addLine} className="mt-1"><Plus size={12} className="mr-1" /> Regel toevoegen</Button>
                  </div>
                </div>

                <div className="col-span-2 grid grid-cols-3 gap-3 mt-3 p-3 rounded-lg bg-muted/30">
                  <Totaal label="Subtotaal" value={fmt(subtotal)} />
                  <Totaal label="BTW" value={fmt(tax)} />
                  <Totaal label="Totaal" value={fmt(subtotal + tax)} accent />
                </div>
                {form.customer_email && (
                  <label className="col-span-2 flex items-center justify-between rounded-lg border border-border px-3 py-2">
                    <span className="text-sm">Direct mailen na opslaan</span>
                    <Input type="checkbox" checked={sendDirect} onChange={(e) => setSendDirect(e.target.checked)} className="h-4 w-4" />
                  </label>
                )}
                {quoteMessage && <div className="col-span-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{quoteMessage}</div>}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>Annuleren</Button>
                <Button onClick={submit} disabled={saving} data-testid="quote-save">{saving ? "Opslaan..." : "Opslaan"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/30">
            <tr className="text-left">
              <th className="px-5 py-3 overline">Nummer</th>
              <th className="px-5 py-3 overline">Klant</th>
              <th className="px-5 py-3 overline">Titel</th>
              <th className="px-5 py-3 overline">Type</th>
              <th className="px-5 py-3 overline">Status</th>
              <th className="px-5 py-3 overline text-right">Totaal</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.map((q) => (
              <tr key={q.id} className="hover:bg-muted/30 transition-colors" data-testid={`quote-row-${q.id}`}>
                <td className="px-5 py-3 font-mono text-xs">{q.number}</td>
                <td className="px-5 py-3">{q.customer_name}</td>
                <td className="px-5 py-3">{q.title}</td>
                <td className="px-5 py-3">{TYPE_LABELS[q.type] || q.type}</td>
                <td className="px-5 py-3"><Badge variant="outline" className={`text-xs ${STATUS_TONE[q.status] || ""}`}>{STATUS_LABELS[q.status] || q.status}</Badge></td>
                <td className="px-5 py-3 text-right font-mono">{fmt(q.total)}</td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-12 text-center text-muted-foreground">Nog geen documenten.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Totaal({ label, value, accent }) {
  return (
    <div className="text-right">
      <div className="overline mb-0.5">{label}</div>
      <div className={`font-mono ${accent ? "text-lg text-primary" : "text-sm"}`}>{value}</div>
    </div>
  );
}

